/**
 * 图片下载器
 * 负责加载图片资源，支持 WebP 降级
 */
export class ImageDownloader {
  /**
   * 下载图片
   * @param url 图片地址
   * @param originalSrc 降级用的原图地址（可选）
   * @returns Promise<HTMLImageElement>
   */
  public static download(url: string, originalSrc?: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const loadUrl = (currentUrl: string, isFallback: boolean) => {
        const imgElem = new Image();
        imgElem.crossOrigin = "anonymous";

        // 【防重复】onload 与 decode 成功可能竞态，用标志位保证只 resolve 一次
        let resolved = false;

        const onLoad = () => {
          if (resolved) return;
          resolved = true;
          // 【P1 修复】resolve 后清理事件监听器，避免 fabric.Image 复用 imgElem 时遗留回调
          imgElem.onload = null;
          imgElem.onerror = null;
          resolve(imgElem);
        };

        const onError = (err?: any) => {
          // 【降级判断】只有真正的加载失败（onerror）才降级换 URL
          // decode 失败不在此列，避免对「数据有效仅解码超限」的误降级
          if (!isFallback && originalSrc && originalSrc !== currentUrl) {
            console.warn(`[ImageLoader] WebP 加载失败，尝试降级加载原图: ${originalSrc}`, err);
            loadUrl(originalSrc, true);
            return;
          }
          reject(err || new Error(`Image load failed: ${currentUrl}`));
        };

        // 【关键】先注册 onload/onerror，再设置 src
        // 确保事件不会在监听器挂载前丢失（同步设置 src 后事件可能很快触发）
        imgElem.onload = onLoad;
        imgElem.onerror = () => onError(new Error(`Image load error: ${currentUrl}`));
        imgElem.src = currentUrl;

        // 【解码策略】decode 作为加速通道：
        // - 成功：提前 resolve，无需等待 onload 事件
        // - 失败：不降级，交由 onload 兜底（浏览器惰性解码，适合超大图）
        //   超大图（如 4608×8192）decode 易触发 EncodingError，但数据本身有效，
        //   onload 仍会正常触发，fabric 使用时再按需解码
        if (imgElem.decode) {
          imgElem.decode().then(onLoad).catch(() => {
            // decode 失败（可能是超大图解码超限），静默等待 onload 兜底
          });
        }
      };

      // 首次尝试加载
      loadUrl(url, false);
    });
  }
}
